/**
 * Centralized badge color definitions with dark mode support.
 * Each color provides light mode + dark mode classes in a single string.
 *
 * IMPORTANT: These preserve all visual distinctions currently in use.
 * Colors are NOT collapsed into fewer categories.
 */

export type BadgeColorKey =
  | 'yellow'
  | 'green'
  | 'emerald'
  | 'blue'
  | 'purple'
  | 'orange'
  | 'amber'
  | 'red'
  | 'gray'
  | 'pink'
  | 'teal'
  | 'indigo'
  | 'cyan'
  | 'slate'
  | 'violet'
  | 'muted';

/**
 * Complete color class strings with dark mode variants.
 * Pattern: bg-[color]-100 text-[color]-800 dark:bg-[color]-900/30 dark:text-[color]-400
 */
export const BADGE_COLORS: Record<BadgeColorKey, string> = {
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  gray: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  pink: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
  teal: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  cyan: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  slate: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
  muted: 'bg-muted text-muted-foreground',
};

/**
 * Badge colors with border variants included
 */
export const BADGE_COLORS_WITH_BORDER: Record<BadgeColorKey, string> = {
  yellow: `${BADGE_COLORS.yellow} border-yellow-200 dark:border-yellow-800`,
  green: `${BADGE_COLORS.green} border-green-200 dark:border-green-800`,
  emerald: `${BADGE_COLORS.emerald} border-emerald-200 dark:border-emerald-800`,
  blue: `${BADGE_COLORS.blue} border-blue-200 dark:border-blue-800`,
  purple: `${BADGE_COLORS.purple} border-purple-200 dark:border-purple-800`,
  orange: `${BADGE_COLORS.orange} border-orange-200 dark:border-orange-800`,
  amber: `${BADGE_COLORS.amber} border-amber-200 dark:border-amber-800`,
  red: `${BADGE_COLORS.red} border-red-200 dark:border-red-800`,
  gray: `${BADGE_COLORS.gray} border-gray-200 dark:border-gray-700`,
  pink: `${BADGE_COLORS.pink} border-pink-200 dark:border-pink-800`,
  teal: `${BADGE_COLORS.teal} border-teal-200 dark:border-teal-800`,
  indigo: `${BADGE_COLORS.indigo} border-indigo-200 dark:border-indigo-800`,
  cyan: `${BADGE_COLORS.cyan} border-cyan-200 dark:border-cyan-800`,
  slate: `${BADGE_COLORS.slate} border-slate-200 dark:border-slate-800`,
  violet: `${BADGE_COLORS.violet} border-violet-200 dark:border-violet-800`,
  muted: `${BADGE_COLORS.muted} border-border`,
};

// =============================================================================
// Domain-Specific Status Mappings
// =============================================================================

/**
 * Job Status → Color mapping
 * Preserves existing color assignments from src/lib/types/job.ts
 */
export const JOB_STATUS_COLORS: Record<string, BadgeColorKey> = {
  draft: 'yellow',
  rfq_open: 'green',
  rfq_closed: 'amber',
  awarded: 'blue',
  in_progress: 'purple',
  completed_pending_review: 'orange',
  accepted: 'emerald',
  rejected: 'red',
  disputed: 'gray',
  closed: 'gray',
};

/**
 * Payment Status → Color mapping
 */
export const PAYMENT_STATUS_COLORS: Record<string, BadgeColorKey> = {
  payment_pending: 'yellow',
  ach_pending: 'orange',
  invoice_pending: 'purple',
  payment_captured: 'blue',
  held_in_escrow: 'blue',
  transfer_initiated: 'purple',
  transfer_complete: 'green',
  refunded: 'gray',
  failed: 'red',
  payment_failed: 'red',
};

/**
 * Milestone Type → Color mapping
 * Matches MilestoneList.tsx getTypeColor()
 */
export const MILESTONE_TYPE_COLORS: Record<string, BadgeColorKey> = {
  planning: 'blue',
  design: 'purple',
  development: 'green',
  review: 'yellow',
  testing: 'orange',
  delivery: 'red',
  client_approval: 'pink',
  installation: 'indigo',
};

/**
 * Milestone Owner → Color mapping
 * Matches MilestoneList.tsx getOwnerColor()
 */
export const MILESTONE_OWNER_COLORS: Record<string, BadgeColorKey> = {
  client: 'blue',
  vendor: 'green',
};

/**
 * PO Status → Color mapping
 */
export const PO_STATUS_COLORS: Record<string, BadgeColorKey> = {
  draft: 'muted',
  sent: 'blue',
  sent_to_vendor: 'blue',
  acknowledged: 'green',
  in_progress: 'yellow',
  modification_requested: 'orange',
  completed: 'emerald',
  closed: 'gray',
  cancelled: 'red',
  current: 'green',
  cancelling: 'orange',
};

/**
 * Reconciliation Status → Color mapping
 */
export const RECONCILIATION_STATUS_COLORS: Record<string, BadgeColorKey> = {
  pending: 'yellow',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
};

/**
 * Vendor Invitation Status → Color mapping
 */
export const VENDOR_INVITATION_STATUS_COLORS: Record<string, BadgeColorKey> = {
  pending: 'yellow',
  accepted: 'green',
  expired: 'gray',
  cancelled: 'red',
};

/**
 * Feedback Status → Color mapping
 */
export const FEEDBACK_STATUS_COLORS: Record<string, BadgeColorKey> = {
  new: 'blue',
  under_review: 'yellow',
  acknowledged: 'purple',
  resolved: 'green',
  wont_fix: 'gray',
};

/**
 * Verification Status → Color mapping
 */
export const VERIFICATION_STATUS_COLORS: Record<string, BadgeColorKey> = {
  verified: 'green',
  pending: 'yellow',
  rejected: 'red',
  expired: 'gray',
};

/**
 * Error Severity → Color mapping
 */
export const SEVERITY_COLORS: Record<string, BadgeColorKey> = {
  low: 'blue',
  medium: 'yellow',
  high: 'orange',
  critical: 'red',
};

/**
 * Account Type → Color mapping (for ledger views)
 */
export const ACCOUNT_TYPE_COLORS: Record<string, BadgeColorKey> = {
  client: 'blue',
  escrow: 'amber',
  platform: 'purple',
  vendor: 'green',
  tax: 'orange',
  cc_fees: 'slate',
  settlement: 'cyan',
};

/**
 * Refund Status → Color mapping
 */
export const REFUND_STATUS_COLORS: Record<string, BadgeColorKey> = {
  requested: 'yellow',
  approved: 'green',
  completed: 'emerald',
  rejected: 'red',
  failed: 'red',
};

/**
 * Deadline Type → Color mapping
 */
export const DEADLINE_TYPE_COLORS: Record<string, BadgeColorKey> = {
  hard: 'red',
  soft: 'yellow',
  flexible: 'green',
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get badge color classes for a color key, with optional border
 */
export function getBadgeColor(
  colorKey: BadgeColorKey | undefined,
  withBorder = false
): string {
  const key = colorKey || 'muted';
  return withBorder ? BADGE_COLORS_WITH_BORDER[key] : BADGE_COLORS[key];
}

/**
 * Get job status badge color
 */
export function getJobStatusColor(status: string, withBorder = false): string {
  const colorKey = JOB_STATUS_COLORS[status] || 'gray';
  return getBadgeColor(colorKey, withBorder);
}

/**
 * Get payment status badge color
 */
export function getPaymentStatusColor(status: string): string {
  const colorKey = PAYMENT_STATUS_COLORS[status] || 'gray';
  return getBadgeColor(colorKey);
}

/**
 * Get milestone type badge color
 */
export function getMilestoneTypeColor(type: string): string {
  const colorKey = MILESTONE_TYPE_COLORS[type] || 'gray';
  return getBadgeColor(colorKey);
}

/**
 * Get milestone owner badge color
 */
export function getMilestoneOwnerColor(owner: string): string {
  const colorKey = MILESTONE_OWNER_COLORS[owner] || 'gray';
  return getBadgeColor(colorKey);
}

/**
 * Get PO status badge color
 */
export function getPOStatusColor(status: string): string {
  const colorKey = PO_STATUS_COLORS[status.toLowerCase()] || 'muted';
  return getBadgeColor(colorKey);
}

/**
 * Get reconciliation status badge color
 */
export function getReconciliationStatusColor(status: string): string {
  const colorKey = RECONCILIATION_STATUS_COLORS[status] || 'gray';
  return getBadgeColor(colorKey);
}

/**
 * Get vendor invitation status badge color
 */
export function getVendorInvitationStatusColor(status: string): string {
  const colorKey = VENDOR_INVITATION_STATUS_COLORS[status] || 'gray';
  return getBadgeColor(colorKey);
}

/**
 * Get refund status badge color
 */
export function getRefundStatusColor(status: string): string {
  const colorKey = REFUND_STATUS_COLORS[status] || 'gray';
  return getBadgeColor(colorKey);
}

/**
 * Get severity badge color
 */
export function getSeverityColor(severity: string): string {
  const colorKey = SEVERITY_COLORS[severity.toLowerCase()] || 'gray';
  return getBadgeColor(colorKey);
}

/**
 * Get account type badge color
 */
export function getAccountTypeColor(account: string): string {
  const colorKey = ACCOUNT_TYPE_COLORS[account] || 'gray';
  return getBadgeColor(colorKey);
}
